export interface AIModel {
  id: string;
  name: string;
  provider: string;
  description: string;
  pricing: string;
  maxTokens?: number;
  features: string[];
  category: 'fast' | 'balanced' | 'powerful' | 'specialized';
}

export const AI_MODELS: AIModel[] = [
  {
    id: "apifreellm",
    name: "FreeLLM (APIFree)",
    provider: "APIFree",
    description: "Hosted chat via apifreellm.com (set APIFREE_LLM_API_KEY)",
    pricing: "Per provider",
    features: ["Bearer auth", "Simple JSON API", "Optional tier info in response"],
    category: "fast",
  },
  // Fast Models (Quick responses, lower cost)
  {
    id: "gemini-2.0-flash",
    name: "Gemini 2.0 Flash",
    provider: "Google",
    description: "Fast and efficient model for quick responses",
    pricing: "$0.4 per 1M tokens",
    features: ["Fast response", "Cost-effective", "General purpose"],
    category: "fast"
  },
  {
    id: "gemini-2.0-flash-lite",
    name: "Gemini 2.0 Flash Lite",
    provider: "Google",
    description: "Ultra-fast model for simple tasks",
    pricing: "$0.3 per 1M tokens",
    features: ["Ultra-fast", "Very cost-effective", "Simple tasks"],
    category: "fast"
  },
  {
    id: "llama-3.1-8b-instant",
    name: "Llama 3.1 8B Instant",
    provider: "Groq",
    description: "Instant responses with Llama 3.1",
    pricing: "$0.05 per 1M tokens",
    features: ["Instant responses", "Very low cost", "Efficient"],
    category: "fast"
  },
  {
    id: "gemma2-9b-it",
    name: "Gemma 2 9B IT",
    provider: "Groq",
    description: "Fast instruction-tuned model",
    pricing: "$0.07 per 1M tokens",
    features: ["Fast", "Instruction-tuned", "Cost-effective"],
    category: "fast"
  },
  {
    id: "claude-3-5-haiku-latest",
    name: "Claude 3.5 Haiku",
    provider: "Anthropic",
    description: "Fast and efficient Claude model for quick tasks",
    pricing: "$1.0 per 1M tokens",
    features: ["Fast response", "Cost-effective", "General purpose"],
    category: "fast"
  },

  // Balanced Models (Good performance, reasonable cost)
  {
    id: "gpt-4o",
    name: "GPT-4o",
    provider: "OpenAI",
    description: "Advanced model with excellent reasoning",
    pricing: "$5.0 per 1M tokens",
    features: ["Advanced reasoning", "Multimodal", "Reliable"],
    category: "balanced"
  },
  {
    id: "gpt-4o-mini",
    name: "GPT-4o Mini",
    provider: "OpenAI",
    description: "Efficient GPT-4o variant",
    pricing: "$0.66 per 1M tokens",
    features: ["Efficient", "Good performance", "Cost-effective"],
    category: "balanced"
  },
  {
    id: "claude-3-7-sonnet-latest",
    name: "Claude 3.7 Sonnet",
    provider: "Anthropic",
    description: "Balanced performance and safety",
    pricing: "$3.0 per 1M tokens",
    features: ["Safety-focused", "Good reasoning", "Reliable"],
    category: "balanced"
  },
  {
    id: "claude-3-5-sonnet-latest",
    name: "Claude 3.5 Sonnet",
    provider: "Anthropic",
    description: "Balanced performance with good reasoning capabilities",
    pricing: "$3.0 per 1M tokens",
    features: ["Good reasoning", "Safety-focused", "Reliable"],
    category: "balanced"
  },
  {
    id: "claude-sonnet-4-20250514",
    name: "Claude Sonnet 4",
    provider: "Anthropic",
    description: "Latest Sonnet model with enhanced capabilities",
    pricing: "$15.0 per 1M tokens",
    features: ["Enhanced reasoning", "Latest model", "Advanced capabilities"],
    category: "balanced"
  },
  {
    id: "gemini-1.5-flash",
    name: "Gemini 1.5 Flash",
    provider: "Google",
    description: "Fast and capable model",
    pricing: "$0.6 per 1M tokens",
    features: ["Fast", "Good performance", "Cost-effective"],
    category: "balanced"
  },
  {
    id: "llama-3.3-70b-versatile",
    name: "Llama 3.3 70B Versatile",
    provider: "Groq",
    description: "Versatile large model",
    pricing: "$0.59 per 1M tokens",
    features: ["Versatile", "Large context", "Good performance"],
    category: "balanced"
  },

  // Powerful Models (High performance, higher cost)
  {
    id: "gpt-4",
    name: "GPT-4",
    provider: "OpenAI",
    description: "Most capable GPT model",
    pricing: "$60.0 per 1M tokens",
    features: ["Highest performance", "Advanced reasoning", "Complex tasks"],
    category: "powerful"
  },
  {
    id: "gemini-2.5-pro",
    name: "Gemini 2.5 Pro",
    provider: "Google",
    description: "Most advanced Gemini model",
    pricing: "$15.0 per 1M tokens",
    features: ["Advanced reasoning", "Multimodal", "Complex tasks"],
    category: "powerful"
  },
  {
    id: "grok-2-latest",
    name: "Grok 2 Latest",
    provider: "xAI",
    description: "Advanced reasoning model",
    pricing: "$10.0 per 1M tokens",
    features: ["Advanced reasoning", "Real-time knowledge", "Complex tasks"],
    category: "powerful"
  },
  {
    id: "o1-mini",
    name: "O1 Mini",
    provider: "OpenAI",
    description: "Advanced reasoning model",
    pricing: "$12.0 per 1M tokens",
    features: ["Advanced reasoning", "Mathematical", "Complex tasks"],
    category: "powerful"
  },
  {
    id: "claude-3-opus-latest",
    name: "Claude 3 Opus",
    provider: "Anthropic",
    description: "Most powerful Claude model for complex tasks",
    pricing: "$15.0 per 1M tokens",
    features: ["Highest performance", "Advanced reasoning", "Complex tasks"],
    category: "powerful"
  },
  {
    id: "claude-opus-4-20250514",
    name: "Claude Opus 4",
    provider: "Anthropic",
    description: "Latest Opus model with maximum capabilities",
    pricing: "$15.0 per 1M tokens",
    features: ["Maximum performance", "Latest model", "Complex reasoning"],
    category: "powerful"
  },

  // Specialized Models
  {
    id: "mistral-large-latest",
    name: "Mistral Large",
    provider: "Mistral",
    description: "Specialized for complex reasoning",
    pricing: "$8.0 per 1M tokens",
    features: ["Complex reasoning", "Code generation", "Specialized"],
    category: "specialized"
  },
  {
    id: "command-r",
    name: "Command R",
    provider: "Cohere",
    description: "Specialized for business applications",
    pricing: "$5.0 per 1M tokens",
    features: ["Business-focused", "Reliable", "Specialized"],
    category: "specialized"
  },
  {
    id: "deepseek-chat",
    name: "DeepSeek Chat",
    provider: "DeepSeek",
    description: "Specialized for coding and reasoning",
    pricing: "$2.0 per 1M tokens",
    features: ["Code generation", "Mathematical", "Specialized"],
    category: "specialized"
  },
  {
    id: "meta.llama3-1-70b-instruct-v1:0",
    name: "Llama 3.1 70B Instruct",
    provider: "Meta",
    description: "Large instruction-tuned model",
    pricing: "$1.0 per 1M tokens",
    features: ["Large context", "Instruction-tuned", "Open source"],
    category: "specialized"
  }
];

export const getModelById = (id: string): AIModel | undefined => {
  return AI_MODELS.find(model => model.id === id);
};

export const getModelsByCategory = (category: AIModel['category']): AIModel[] => {
  return AI_MODELS.filter(model => model.category === category);
};

export const getDefaultModel = (): AIModel => {
  return AI_MODELS.find(model => model.id === "gemini-2.0-flash") || AI_MODELS[0];
};
